import { forwardRef } from "react";
import { flexRender } from "@tanstack/react-table";
import { useAtomValue } from "jotai";
import { CSS } from "@dnd-kit/utilities";
import {
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from "@dnd-kit/sortable";

import { IsMobileAtom } from "../../App";

export const THeader = forwardRef<HTMLTableCellElement, any>(
  (
    {
      isDragging,
      isPlaceholder,
      columnID,
      isMobile,
      pinned,
      i,
      renderValue,
      headerGroup,
      style,
      className,
      addDisabled,
      ...props
    },
    ref
  ) => {
    return (
      <th
        className={`gc-table-th ${isDragging ? "dragging" : ""} ${
          columnID === "selectable-rows" ||
          (!isMobile && columnID === "_RecordName")
            ? `gc-table-fixed-header ${columnID}`
            : "pl-2 pr-2 py-2"
        } ${columnID === "column-add" ? "w-100" : ""} ${
          i === 0 ? "gc-table-th-first" : ""
        } ${i === headerGroup?.headers?.length - 1 ? "gc-table-th-last" : ""} ${
          pinned && i === 0 ? "gc-table-th-first" : ""
        } ${className ? className : ""}`}
        {...props}
        ref={ref}
        style={style}
        scope="col"
      >
        {isPlaceholder || (columnID === "column-add" && addDisabled) ? null : (
          <>
            {columnID === "selectable-rows" || columnID === "_RecordName" ? (
              <div
                className={`inner ${
                  columnID === "selectable-rows" ? "px-3 py-2" : ""
                } ${pinned && i === 1 ? "gc-table-pinned" : ""}`}
              >
                {renderValue}
              </div>
            ) : (
              renderValue
            )}
          </>
        )}
      </th>
    );
  }
);

const DraggableTableHeader = ({
  header,
  isMobile,
  pinned,
  i,
  renderValue,
  headerGroup,
}: {
  header: any;
  isMobile: boolean;
  pinned: boolean;
  i: number;
  renderValue: any;
  headerGroup: any;
}) => {
  const columnID = header?.column?.id;

  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: columnID,
      disabled:
        columnID === "selectable-rows" ||
        columnID === "_RecordName" ||
        columnID === "column-add",
    });

  const style: React.CSSProperties = {
    opacity: isDragging ? 0.9 : 1,
    background: isDragging ? "var(--dropdown-selected)" : undefined,
    transform: CSS.Translate.toString(transform), // translate instead of transform to avoid squishing
    transition: "width transform 0.2s ease-in-out",
    whiteSpace: "nowrap",
    width: header.column.getSize(),
    cursor: "grab",
  };

  return (
    <THeader
      isPlaceholder={header?.isPlaceholder}
      columnID={columnID}
      isMobile={isMobile}
      pinned={pinned}
      i={i}
      renderValue={renderValue}
      headerGroup={headerGroup}
      style={style}
      {...attributes}
      {...listeners}
      ref={setNodeRef}
    />
  );
};

interface Props {
  table: any;
  pinned: boolean;
  items: any;
  dragDisabled?: boolean;
}

const AppTableHeaders = ({ table, pinned, items, dragDisabled }: Props) => {
  const isMobile = useAtomValue(IsMobileAtom);

  return (
    <thead style={{ zIndex: 1, height: 41 }} className="gc-table-thead">
      {table.getHeaderGroups().map((headerGroup: any) => {
        return (
          <tr style={{ zIndex: 1 }} key={headerGroup.id}>
            <SortableContext
              items={items || []}
              strategy={horizontalListSortingStrategy}
            >
              {headerGroup.headers.map((header: any, i: number) => {
                let renderValue = flexRender(
                  header?.column?.columnDef?.header,
                  header?.getContext()
                );

                if (
                  dragDisabled ||
                  ["_RecordName", "selectable-rows", "column-add"].includes(
                    header?.column?.id
                  )
                ) {
                  return (
                    <THeader
                      key={header?.column?.id}
                      isPlaceholder={header?.isPlaceholder}
                      columnID={header?.column?.id}
                      i={i}
                      pinned={pinned}
                      isMobile={isMobile}
                      renderValue={renderValue}
                      headerGroup={headerGroup}
                      addDisabled={dragDisabled}
                    />
                  );
                }

                return (
                  <DraggableTableHeader
                    key={header?.column?.id}
                    header={header}
                    i={i}
                    pinned={pinned}
                    isMobile={isMobile}
                    renderValue={renderValue}
                    headerGroup={headerGroup}
                  />
                );
              })}
            </SortableContext>
          </tr>
        );
      })}
    </thead>
  );
};

export default AppTableHeaders;
